/**
 * The DebugOverlay component provides a debug panel that can be toggled
 * on and off. It provides testing tools for QAs and developers
 * No translations are needed for this component
 */
import React, { useState, useEffect, useRef } from 'react';
import styles from './debug-overlay.module.scss';
import { getSubdomainCookie } from 'state/session/helpers';
import classNames from 'classnames';
import { storageKeys } from 'globals/storage';
import { motion } from 'framer-motion';
import * as cookieManager from 'globals/cookieManager';
import { getRootHost } from 'globals/helpers';
import { AppLogger, DatadogClient } from '@workivate/tho-web-shared';
import { getStore } from 'app/hermes-redux';
import { logout } from 'state/session/actions';
import isEmpty from 'lodash/isEmpty';

const currentDomain = '.' + getRootHost();

export const getEnabledLogFeatures = () => {
  const enabledLogFeatures = localStorage.getItem('enabledLogFeatures');
  return enabledLogFeatures ? JSON.parse(enabledLogFeatures) : {};
};

if (
  !IS_PRODUCTION &&
  localStorage.getItem('debugLogsOn') === 'true' &&
  isEmpty(getEnabledLogFeatures())
) {
  if (localStorage.getItem('enabledLogFeatures')) {
    localStorage.setItem(
      'enabledLogFeatures',
      JSON.stringify({ oauth2: true }),
    );
  }
}

const handleLogFeaturesChange = (key: string, value: boolean) => {
  const enabledLogFeatures = getEnabledLogFeatures();
  enabledLogFeatures[key] = value;
  localStorage.setItem(
    'enabledLogFeatures',
    JSON.stringify(enabledLogFeatures),
  );
  if (key === storageKeys.debugLogsOn && value === false) {
    localStorage.removeItem('enabledLogFeatures');
  }
};

const DebugLogs = () => {
  const enabledLogFeaturesString = JSON.stringify(getEnabledLogFeatures());
  useEffect(() => {
    const enabledLogFeatures = getEnabledLogFeatures();
    if (enabledLogFeatures.oauth2) {
      AppLogger.setEnabledFeatures({ oauth2: true });
    } else {
      AppLogger.setEnabledFeatures({ oauth2: false });
    }
  }, [enabledLogFeaturesString]);

  const debugLogsOn = localStorage.getItem(storageKeys.debugLogsOn) === 'true';

  return (
    debugLogsOn && (
      <ul style={{ marginLeft: '20px' }}>
        <li>
          <input
            type='checkbox'
            defaultChecked={getEnabledLogFeatures().oauth2}
            onChange={e => handleLogFeaturesChange('oauth2', e.target.checked)}
          />
          oauth2
        </li>
      </ul>
    )
  );
};

interface LocalStoragePanelProps {
  handleCheckboxChange: (key: string, value: boolean) => void;
}

const LocalStoragePanel: React.FC<LocalStoragePanelProps> = ({
  handleCheckboxChange,
}) => {
  const settings = [
    storageKeys.stayOnGenericSubdomain,
    storageKeys.isUnifiedLoginEnabled,
    storageKeys.dataDogEnabled,
    storageKeys.debugLogsOn,
    storageKeys.showDebugOverlayButton,
  ];

  return (
    <div className={styles.panelContents}>
      <ul>
        {settings.map(setting => {
          const initialValue = localStorage.getItem(setting) === 'true';
          return (
            <li key={setting}>
              <label>
                <input
                  type='checkbox'
                  defaultChecked={initialValue}
                  name={setting}
                  id={setting}
                  onChange={e =>
                    handleCheckboxChange(setting, e.target.checked)
                  }
                />
                {setting}
              </label>
              {setting === storageKeys.debugLogsOn && initialValue && (
                <DebugLogs />
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

interface CookiePanelProps {
  removeSubdomainCookie: () => void;
}

const CookiePanel: React.FC<CookiePanelProps> = ({ removeSubdomainCookie }) => {
  if (!getSubdomainCookie()) {
    return null;
  }
  return (
    <div className={classNames(styles.panelContents, styles.cookiePanelDiv)}>
      <h3>Cookie Panel</h3>
      <ul>
        <li>
          <div>
            WAM_SUBDOMAIN: {getSubdomainCookie()}
            <button
              onClick={removeSubdomainCookie}
              className={styles.removeButton}
            >
              delete
            </button>
          </div>
        </li>
      </ul>
    </div>
  );
};

interface ActionsPanelProps {
  isOnLocalhost: boolean;
  openUSLRedirect: () => void;
  disableUSLRedirect: () => void;
  enableSSOXZeus: () => void;
  disableSSOXZeus: () => void;
}

const ActionsPanel: React.FC<ActionsPanelProps> = ({
  isOnLocalhost,
  openUSLRedirect,
  disableUSLRedirect,
  enableSSOXZeus,
  disableSSOXZeus,
}) => {
  const tokenRef = useRef<HTMLInputElement>(null);
  const selectRef = useRef<HTMLSelectElement>(null);

  return (
    <div className={styles.panelContents}>
      {isOnLocalhost && (
        <div className={styles.actionColumn}>
          <div className={styles.buttonRow}>
            <button id='redirect-usl-btn' onClick={openUSLRedirect}>
              Set USL to redirect to localhost
            </button>
            <button onClick={disableUSLRedirect}>
              Disable USL local redirect
            </button>
          </div>
          <div>
            <small>
              <i>This action is only visible on localhost</i>
            </small>
          </div>
        </div>
      )}
      <div className={styles.actionColumn}>
        <div className={styles.buttonRow}>
          <button id='enable-ssox-btn' onClick={enableSSOXZeus}>
            *Enable SSOX from Zeus to Hermes
          </button>
          <button onClick={disableSSOXZeus}>
            Disable SSOX from Zeus to Hermes
          </button>
        </div>
        <div>
          <small>
            <i>
              * SSOX (Ghost login) is a temporaryily toggleable , once Hercules
              is shipped it will be the default behaviour
            </i>
          </small>
        </div>
      </div>

      <div className={classNames(styles.actionColumn, styles.ottInput)}>
        <select
          name='tokenSelect'
          className={styles.tokenSelect}
          ref={selectRef}
        >
          <option value='x-usl-ott'>x-usl-ott</option>
          <option value='token'>token</option>
          <option value='userTokenOtt'>userToken&ott</option>
          <option value='deeplink'>deeplink</option>
        </select>
        <input ref={tokenRef} type='text' placeholder='Enter Token' />
        <button
          id='login-token-btn'
          onClick={() => {
            if (tokenRef.current) {
              // Get the selected option value
              const selectedOption = selectRef.current?.value.trim();
              const tokenValue = tokenRef.current?.value.trim();
              const newLocation = new URL(window.location.href);

              switch (selectedOption) {
                case 'x-usl-ott':
                  newLocation.searchParams.set('x-usl-ott', tokenValue);
                  break;
                case 'token':
                  newLocation.searchParams.set('token', tokenValue);
                  break;
                case 'userTokenOtt':
                  newLocation.searchParams.set('userToken', tokenValue);
                  newLocation.searchParams.set('ott', 'true');
                  break;
                case 'deeplink':
                  newLocation.searchParams.set('userToken', tokenValue);
                  break;
              }

              window.location.href = newLocation.href;
            }
          }}
        >
          Login with Token
        </button>
      </div>
      <button
        id='logout-btn'
        onClick={async () => await getStore().dispatch(logout(false))}
      >
        Logout
      </button>
    </div>
  );
};

const ErrorTestingPanel: React.FC = () => {
  const [customErrorMessage, setCustomErrorMessage] = useState(
    'Test error from debug panel',
  );
  const [isErrorSent, setIsErrorSent] = useState(false);

  const triggerDatadogError = () => {
    // Create a custom error with the user-provided message
    const customError = new Error(customErrorMessage);
    customError.name = 'DebugPanelTestError';
    customError.stack = `DebugPanelTestError: ${customErrorMessage}\n    at triggerDatadogError (debug-overlay.tsx)\n    at onClick (debug-overlay.tsx)`;

    // Send error to Datadog RUM
    DatadogClient.addError(customError);

    // Also log a message for additional context
    DatadogClient.logMessage(`Debug Panel Test Error: ${customErrorMessage}`, {
      tags: ['source:debug-panel', 'type:manual-test'],
    });

    setIsErrorSent(true);
    setTimeout(() => setIsErrorSent(false), 3000);
  };

  const triggerJavaScriptError = () => {
    // Trigger a real JavaScript error that will be caught by error boundaries
    const errorMessage =
      customErrorMessage || 'Test JavaScript error from debug panel';
    throw new Error(errorMessage);
  };

  return (
    <div className={styles.panelContents}>
      <div className={styles.errorInputSection}>
        <label htmlFor='error-message-input'>Custom Error Message:</label>
        <input
          id='error-message-input'
          type='text'
          value={customErrorMessage}
          onChange={e => setCustomErrorMessage(e.target.value)}
          placeholder='Enter custom error message'
          className={styles.fullInput}
        />
      </div>

      <div className={styles.buttonRow}>
        <button
          onClick={triggerDatadogError}
          className={styles.errorTestButton}
          title='Sends error directly to Datadog RUM without throwing'
        >
          {isErrorSent ? '✓ Error Sent!' : 'Send RUM Error'}
        </button>

        <button
          onClick={triggerJavaScriptError}
          className={styles.errorTestButton}
          title='Throws a real JavaScript error that will be caught by error boundaries'
        >
          Throw JS Error
        </button>
      </div>

      <div className={styles.errorTestInfo}>
        <small>
          <strong>RUM Error:</strong> Sends error directly to Datadog without
          throwing
          <br />
          <strong>JS Error:</strong> Throws actual error (may crash component)
        </small>
      </div>
    </div>
  );
};

interface CopyPanelProps {
  goToDatadog: (query: string) => void;
}

const CopyPanel: React.FC<CopyPanelProps> = ({ goToDatadog }) => {
  const copyPanelKeys = [
    '_auth_clientId',
    '_auth_idToken',
    '_auth_refreshToken',
    'session_id',
    'dd_client_id_query',
  ];
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  let clientId: string | undefined;
  let sessionId: string | undefined;

  const copyToClipboard = (key: string, value: string) => {
    navigator.clipboard.writeText(value);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  return (
    <div className={styles.panelContents}>
      <ul>
        {copyPanelKeys.map(key => {
          let value;
          if (key === 'session_id') {
            value = DatadogClient.getSessionId();
            sessionId = value;
          } else {
            value = localStorage.getItem(key);
          }

          if (key === '_auth_clientId' && value) {
            clientId = value;
          }

          if (key === 'dd_client_id_query' && clientId) {
            value = `https://app.datadoghq.com/logs?query=@public_client_id:${clientId}`;
          }

          return (
            value && (
              <li key={key}>
                <span>{key}: </span>
                <input
                  type='text'
                  value={value}
                  readOnly
                  className={styles.fullInput}
                  title={value}
                />

                <button
                  onClick={() => copyToClipboard(key, value)}
                  className={styles.copyPanelButton}
                >
                  {copiedKey === key ? '✔' : 'Copy'}
                </button>
              </li>
            )
          );
        })}
      </ul>
      <div className={styles.buttonRow}>
        {!!clientId && (
          <button
            className={styles.datadogButton}
            onClick={() => {
              goToDatadog(`@public_client_id:${clientId}`);
            }}
          >
            Find Datadog logs with client id
          </button>
        )}
        {!!sessionId && (
          <button
            className={styles.datadogButton}
            onClick={() => {
              goToDatadog(`@session_id:${sessionId}`);
            }}
          >
            Find Datadog logs with session id
          </button>
        )}
      </div>
    </div>
  );
};

const DebugOverlay: React.FC = () => {
  const isOnLocalhost = window.origin.includes('localhost');
  const isOnTest = window.origin.includes('test');

  const [showDebugOverlay, setShowDebugOverlay] = useState(
    localStorage.getItem(storageKeys.showDebugOverlay) === 'true',
  );
  const [showDebugOverlayButton, setShowDebugOverlayButton] = useState(
    localStorage.getItem(storageKeys.showDebugOverlayButton) === 'true',
  );

  const toggleOverlay = (booleanValue?: boolean | undefined) => {
    if (booleanValue !== undefined) {
      setShowDebugOverlay(booleanValue);
      localStorage.setItem(
        storageKeys.showDebugOverlay,
        booleanValue.toString(),
      );
    } else {
      setShowDebugOverlay(prev => {
        const newValue = !prev;
        localStorage.setItem(storageKeys.showDebugOverlay, newValue.toString());
        return newValue;
      });
    }
  };

  const handleCheckboxChange = (key: string, value: boolean) => {
    const noReloadKeys: string[] = [
      storageKeys.showDebugOverlayButton,
      storageKeys.stayOnGenericSubdomain,
    ];

    localStorage.setItem(key, value.toString());
    if (key === storageKeys.stayOnGenericSubdomain && value === true) {
      cookieManager.deleteCookie('WAM_SUBDOMAIN', currentDomain);
    }
    if (key === storageKeys.debugLogsOn && value === false) {
      localStorage.removeItem('enabledLogFeatures');
    }

    if (key === storageKeys.showDebugOverlayButton) {
      setShowDebugOverlayButton(value);
    }

    if (key === storageKeys.isUnifiedLoginEnabled && value) {
      // let's also enable the oauth2 logging feature when we set USL to enabled
      const enabledLogFeatures = getEnabledLogFeatures();
      enabledLogFeatures.oauth2 = value;
      localStorage.setItem(
        'enabledLogFeatures',
        JSON.stringify(enabledLogFeatures),
      );
      AppLogger.setEnabledFeatures({ oauth2: value });
    }

    // Exclude keys that don't require a reload
    if (!noReloadKeys.includes(key)) {
      window.location.reload();
    }
  };

  const openUSLRedirect = () => {
    window.open(
      'https://usl.enterprise.test.lifeworks.com?redirectTo=development',
      '_blank',
      'noopener',
    );
  };

  const disableUSLRedirect = () => {
    window.open(
      'https://usl.enterprise.test.lifeworks.com?redirectTo=false',
      '_blank',
      'noopener',
    );
  };

  const enableSSOXZeus = () => {
    if (isOnLocalhost) {
      window.open(
        'http://localhost:3001?enableTestableFeatures=true&isSSOXEnabled=true',
        '_blank',
        'noopener',
      );
    } else if (isOnTest) {
      window.open(
        'https://admin.test.lifeworks.com?enableTestableFeatures=true&isSSOXEnabled=true',
        '_blank',
        'noopener',
      );
    }
  };

  const disableSSOXZeus = () => {
    if (isOnLocalhost) {
      window.open(
        'http://localhost:3001?enableTestableFeatures=true&isSSOXEnabled=false',
        '_blank',
        'noopener',
      );
    } else if (isOnTest) {
      window.open(
        'https://admin.test.lifeworks.com?enableTestableFeatures=true&isSSOXEnabled=false',
        '_blank',
        'noopener',
      );
    }
  };

  const removeSubdomainCookie = () => {
    cookieManager.deleteCookie('WAM_SUBDOMAIN', currentDomain);

    window.location.reload();
  };

  const goToDatadog = (query: string) => {
    const datadogClientIdUrl = `https://app.datadoghq.com/logs?query=${query}`;
    window.open(datadogClientIdUrl, '_blank');
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.code === 'KeyD' && event.shiftKey) {
        toggleOverlay(undefined);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div>
      {showDebugOverlayButton === true && (
        <div
          className={styles.overlayButton}
          onClick={() => toggleOverlay()}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              toggleOverlay();
            }
          }}
          role='button'
          tabIndex={0}
          title='Debug Panel (Shift + D)'
        >
          🛠️
        </div>
      )}
      {!!showDebugOverlay && (
        <motion.div
          drag='y'
          className={styles.overlay}
          dragMomentum={false}
          dragElastic={0}
        >
          <div className={styles.header}>
            <h2>Debug Panel</h2>

            {/* Add debugging information here */}
            <button
              className={styles['container__close-button']}
              onClick={() => toggleOverlay(false)}
            >
              {' '}
              x{' '}
            </button>
          </div>
          <div className={styles.content}>
            <div className={styles.storagePanel}>
              <h3>Local Storage Panel</h3>
              <LocalStoragePanel handleCheckboxChange={handleCheckboxChange} />
              <CookiePanel removeSubdomainCookie={removeSubdomainCookie} />
            </div>
            <div className={styles.actionsPanel}>
              <h3>Actions Panel</h3>
              <ActionsPanel
                isOnLocalhost={isOnLocalhost}
                openUSLRedirect={openUSLRedirect}
                disableUSLRedirect={disableUSLRedirect}
                enableSSOXZeus={enableSSOXZeus}
                disableSSOXZeus={disableSSOXZeus}
              />
            </div>
            <div className={styles.copyPanel}>
              <h3>Copy Panel</h3>
              <CopyPanel goToDatadog={goToDatadog} />
            </div>
            <div className={styles.errorTestingPanel}>
              <h3>Error Testing Panel</h3>
              <ErrorTestingPanel />
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default DebugOverlay;
